let _ = require('lodash');

module.exports = {
    dfsRecursive,
    initTreeUiState,
    changeTreeNodeValue,
    getLeafs,
    setAllSelection,
    getSelectedLeafs,
    restoreSelectedLeafsState,
    selectLeafs,
    clearFilter,
    selectBranch,
    setValueByFilter
};

function getSelectedLeafs (leafs, selected) {
    return _.filter(leafs, (x) => {
        let state = selected && selected[x];
        return state && state.value;
    });
}

function changeTreeNodeValue (state, originalDataPath, basePath, nodeId, value) {
    let tree = state.get(originalDataPath);
    let selectedDataPath = basePath.concat('selected');

    let selected = _.cloneDeep(state.get(selectedDataPath));
    updateSelected(selected, tree, nodeId, value);

    let node = state.get(originalDataPath.concat(nodeId));
    if (node && node.parentCode) {
        propagateCheckedToAscendants(state, tree, selected, node.parentCode);
    }

    state.set(selectedDataPath, selected);
}

function setAllSelection (state, originalDataPath, basePath, value) {
    let nodeState = { value: value, indeterminate: false };
    let selected = _.mapValues(state.get(originalDataPath), () => nodeState);
    state.set(basePath.concat('selected'), selected);
}

function selectBranch (state, originalDataPath, basePath, value, branchId) {
    let selectedDataPath = basePath.concat('selected');
    let selected = _.cloneDeep(state.get(selectedDataPath));
    let branchedNodes = _.filter(state.get(originalDataPath), { 'branchId': branchId });
    let tree = state.get(originalDataPath);

    let parents = [];
    _.forEach(branchedNodes, (x) => {
        updateSelected(selected, tree, x.code, value);
        parents.push(x.parentCode);
    });

    parents = _.uniqBy(parents, x => x);
    _.forEach(parents, (x) => {
        propagateCheckedToAscendants(state, tree, selected, x);
    });

    state.set(basePath.concat('selected'), selected);
}

function setValueByFilter (state, originalDataPath, basePath, value, filter) {
    let selectedDataPath = basePath.concat('selected');
    let selected = _.cloneDeep(state.get(selectedDataPath));
    let branchedNodes = _.filter(state.get(originalDataPath), filter);
    let tree = state.get(originalDataPath);

    let parents = [];
    _.forEach(branchedNodes, (x) => {
        updateSelected(selected, tree, x.code, value);
        parents.push(x.parentCode);
    });

    parents = _.uniqBy(parents, x => x);
    _.forEach(parents, (x) => {
        propagateCheckedToAscendants(state, tree, selected, x);
    });

    state.set(basePath.concat('selected'), selected);
}

function initTreeUiState (state, originalDataPath, basePath) {
    let UI_STATE = { 0: true };

    state.set(basePath.concat('expanded'), UI_STATE);
    state.set(basePath.concat('filter'), null);
    setAllSelection(state, originalDataPath, basePath, false);
}

function dfsRecursive (tree, nodeId, cb) {
    let node = tree[nodeId];
    let children = node.children || [];

    _.forEach(children, (x) => dfsRecursive(tree, x, cb));
    cb(node);
}

function ascendRecursive (tree, nodeId, cb) {
    if (nodeId === null) {
        return;
    }

    let node = tree[nodeId];
    if (cb(node)) {
        return;
    }

    ascendRecursive(tree, node.parentCode, cb);
}

function getLeafs (tree) {
    return _.chain(tree).filter((node) => node.children.length === 0).map('code').value();
}

function updateSelected (selected, tree, nodeId, value) {
    dfsRecursive(tree, nodeId, (node) => {
        selected[node.code] = Object.assign({ value: value, indeterminate: false });
    });
}

function propagateCheckedToDescendants (state, tree, selectedDataPath, nodeId, value) {
    dfsRecursive(tree, nodeId, (node) => {
        state.set(selectedDataPath.concat(node.code), Object.assign({ value: value, indeterminate: false }));
    });
}

function propagateCheckedToAscendants (state, tree, selected, nodeId) {
    ascendRecursive(tree, nodeId, (node) => {
        if (node.code === '0') {
            return true;
        }

        selected[node.code] = calculateNodeSelectionState(selected, node);
    });
}

function calculateNodeSelectionState (selectionState, node) {
    if (node.children.length === 0) {
        return selectionState[node.code];
    }

    let childrenState = _.map(node.children, (x) => selectionState[x]);
    let haveChecked = _.findIndex(childrenState, ['value', true]);
    let haveUnchecked = _.findIndex(childrenState, ['value', false]);
    if (haveChecked > -1 && haveUnchecked > -1) {
        // если есть и отмеченный дочерний элемент и неотмеченный, то сосотояние родителя неопределённое
        return { value: true, indeterminate: true };
    }
    let haveIndeterminate = _.findIndex(childrenState, ['indeterminate', true]);
    if (haveIndeterminate > -1) {
        // если есть неопределённый дочерний элемент, то родитель тоже неопределённый
        return {value: true, indeterminate: true};
    } else if (haveChecked > -1) {
        // если все дочерние отмечены, то родитель отмечен
        return { value: true, indeterminate: false };
    } else {
        // если все дочерние неотмеченные, то родитель неотмеченный
        return { value: false, indeterminate: false };
    }
}

function restoreSelectedLeafsState (state, originalDataPath, selectedDataPath, selectedLeafs) {
    let tree = state.get(originalDataPath);

    let defaultNodeSate = { value: false, indeterminate: false };
    let selectionState = _.mapValues(tree, () => Object.assign({}, defaultNodeSate));

    _.forEach(selectedLeafs, (nodeId) => {
        if (tree.hasOwnProperty(nodeId)) {
            dfsRecursive(tree, nodeId, (node) => {
                selectionState[node.code] = Object.create({ value: true, indeterminate: false });
            });
        } else {
            state.set(['order', 'localStorageSelectedParamsWasOutdated'], true);
        }
    });

    dfsRecursive(tree, '0', (node) => {
        let newState = calculateNodeSelectionState(selectionState, node);
        selectionState[node.code] = newState;
    });

    state.set(selectedDataPath, selectionState);
}

function selectLeafs (state, originalDataPath, selectedDataPath, selectedLeafs) {
    let tree = state.get(originalDataPath);
    let selectionState = _.cloneDeep(state.get(selectedDataPath));

    _.forEach(selectedLeafs, (nodeId) => {
        if (tree.hasOwnProperty(nodeId)) {
            dfsRecursive(tree, nodeId, (node) => {
                selectionState[node.code] = Object.create({ value: true, indeterminate: false });
            });
        } else {
            state.set(['order', 'localStorageSelectedParamsWasOutdated'], true);
        }
    });

    dfsRecursive(tree, '0', (node) => {
        let newState = calculateNodeSelectionState(selectionState, node);
        selectionState[node.code] = newState;
    });

    state.set(selectedDataPath, selectionState);
}

function clearFilter (state, basePath, treeId) {
    state.set(basePath.concat('filter'), null);
    let htmlInput = document.getElementById(treeId+'Filter');
    if (htmlInput) {
        htmlInput.value = '';
    }
}



// WEBPACK FOOTER //
// ./helpers/tree.js